import { useState, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';

/**
 * Specialized infinite scroll hook for assignments page
 * Based on the proven Schedule.js implementation but adapted for assignments workflow
 * 
 * @param {Object} config Configuration object
 * @param {DocumentNode} config.query - GraphQL query (GET_JOBS_FOR_ASSIGNMENTS)
 * @param {Object} config.variables - Base query variables
 * @param {number} config.pageSize - Items per page (default: 20)
 * @param {boolean} config.enabled - Whether the query should run
 * @param {Function} config.onError - Error handler function
 * @returns {Object} Hook return object
 */
export function useAssignmentsInfiniteScroll({
  query,
  variables = {},
  pageSize = 20,
  enabled = true,
  onError = () => {}
}) {
  // Pagination state - mirrors Schedule.js implementation
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [allJobsLoaded, setAllJobsLoaded] = useState(false);
  
  // Refs for throttling - same as Schedule.js
  const lastLoadTime = useRef(0);
  const isLoadingMoreRef = useRef(false);
  
  // Prepare query variables with pagination
  const queryVariables = useMemo(() => ({
    ...variables,
    input: {
      ...variables.input,
      limit: pageSize,
      offset: 0 // Always start from 0 for initial query
    }
  }), [variables, pageSize]);
  
  // Main query - optimized for performance
  const {
    data,
    loading,
    error,
    refetch,
    fetchMore,
    networkStatus
  } = useQuery(query, {
    variables: queryVariables,
    skip: !enabled,
    fetchPolicy: 'cache-first', // Use cache-first for better performance
    errorPolicy: 'partial',
    notifyOnNetworkStatusChange: false, // Disable to reduce re-renders
    returnPartialData: true,
    onError: (error) => {
      // Only log actual errors, not cancellations
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        onError(error, 'assignments jobs');
      }
    }
  });
  
  // Extract and process data
  const jobsData = data?.jobsForAssignments;
  const allJobs = jobsData?.items || [];
  const unassignedJobs = useMemo(() => {
    // Filter out jobs that are assigned to either an individual user or a team
    return allJobs.filter(job => !job.assignedTo?.id && !job.assignedTeamId);
  }, [allJobs]);
  
  // Update pagination state when data changes
  useMemo(() => {
    if (jobsData) {
      const hasMoreData = jobsData.hasMore || false;
      const total = jobsData.total || 0;
      const unassignedCount = jobsData.unassigned || 0;
      
      setHasMore(hasMoreData);
      setTotalItems(total);
      
      // Check if all jobs are loaded
      if (!hasMoreData || allJobs.length >= total) {
        setAllJobsLoaded(true);
      }
    }
  }, [jobsData, allJobs.length]);
  
  // Infinite scroll load more function - enhanced version from Schedule.js
  const handleLoadMore = useCallback((limit = pageSize) => {
    // Prevent multiple simultaneous requests - same logic as Schedule.js
    if (loading || isLoadingMore || !hasMore || allJobsLoaded) {
      return;
    }
    
    // Throttle requests to prevent excessive API calls - same as Schedule.js
    const now = Date.now();
    if (now - lastLoadTime.current < 1000) { // 1 second throttle
      return;
    }
    
    if (isLoadingMoreRef.current) {
      return;
    }
    
    lastLoadTime.current = now;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    
    // Calculate new offset
    const newOffset = currentOffset + limit;
    
    // Use fetchMore to append new data - same pattern as Schedule.js
    fetchMore({
      variables: {
        ...queryVariables,
        input: {
          ...queryVariables.input,
          offset: newOffset,
          limit: limit
        }
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.jobsForAssignments?.items?.length) {
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
          setAllJobsLoaded(true);
          return prev;
        }
        
        const newJobs = fetchMoreResult.jobsForAssignments.items;
        const existingJobs = prev.jobsForAssignments?.items || [];
        
        // Filter out duplicates - same logic as Schedule.js
        const uniqueNewJobs = newJobs.filter(newJob => 
          !existingJobs.some(existingJob => existingJob.id === newJob.id)
        );
        
        // Update offset for next load
        setCurrentOffset(newOffset);
        
        
        if (uniqueNewJobs.length === 0) {
          setIsLoadingMore(false);
          isLoadingMoreRef.current = false;
          setAllJobsLoaded(true);
          return prev;
        }
        
        // Combine existing and new jobs - same structure as Schedule.js
        const combinedResult = {
          ...prev,
          jobsForAssignments: {
            ...prev.jobsForAssignments,
            items: [...existingJobs, ...uniqueNewJobs],
            hasMore: fetchMoreResult.jobsForAssignments.hasMore,
            total: fetchMoreResult.jobsForAssignments.total,
            assigned: fetchMoreResult.jobsForAssignments.assigned,
            unassigned: fetchMoreResult.jobsForAssignments.unassigned,
            // Only include nextCursor if it exists in the response
            ...(fetchMoreResult.jobsForAssignments.nextCursor && {
              nextCursor: fetchMoreResult.jobsForAssignments.nextCursor
            })
          }
        };
        
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
        
        return combinedResult;
      }
    }).catch((error) => {
      // Only log non-cancellation errors
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        onError(error, 'load more assignments');
      }
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    });
  }, [
    hasMore, 
    loading, 
    isLoadingMore, 
    allJobsLoaded,
    currentOffset, 
    fetchMore, 
    queryVariables, 
    pageSize,
    onError
  ]);
  
  // Refetch all data (reset pagination) - same as Schedule.js
  const refetchAll = useCallback(async (forceNetworkOnly = false) => {
    try {
      // Reset pagination state
      setCurrentOffset(0);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      setHasMore(true);
      setAllJobsLoaded(false);
      
      // Refetch with original variables
      await refetch({
        ...queryVariables,
        input: {
          ...queryVariables.input,
          offset: 0,
          limit: pageSize
        }
      });
    } catch (error) {
      onError(error, 'refetch assignments');
      throw error;
    }
  }, [refetch, queryVariables, pageSize, onError]);
  
  // Auto-backfill logic - conservative like Schedule.js
  const autoBackfillIfNeeded = useCallback((assignedJobCount = 0) => {
    if (!hasMore || isLoadingMore || allJobsLoaded) {
      return;
    }
    
    const currentUnassignedCount = unassignedJobs.length;
    const MIN_VISIBLE_JOBS = 10; // Conservative minimum like Schedule.js
    
    // Conservative auto-loading logic - only when really needed
    const shouldLoadMore = (
      // Case 1: No jobs visible at all
      currentUnassignedCount === 0 ||
      // Case 2: After assignments, maintain minimum visible
      (assignedJobCount > 0 && currentUnassignedCount < MIN_VISIBLE_JOBS)
    );
    
    if (shouldLoadMore) {
      // Always load standard 20 jobs like Schedule.js
      const jobsToLoad = Math.max(assignedJobCount, 20);
      
      
      handleLoadMore(jobsToLoad);
    }
  }, [hasMore, isLoadingMore, allJobsLoaded, unassignedJobs.length, handleLoadMore]);
  
  // Calculate display counts
  const displayedItemsCount = allJobs.length;
  const unassignedCount = unassignedJobs.length;
  
  // Job counts object - matches the existing totalJobsCount structure
  const jobCounts = useMemo(() => {
    return {
      total: jobsData?.total || 0,
      assigned: jobsData?.assigned || 0,
      unassigned: jobsData?.unassigned || 0
    };
  }, [jobsData]);
  
  return {
    // Data
    allJobs,
    unassignedJobs,
    jobsData,
    
    // Counts
    jobCounts,
    totalItems,
    displayedItemsCount,
    unassignedCount,
    
    // Loading states
    loading: loading && displayedItemsCount === 0, // Initial load
    isLoadingMore,
    error,
    
    // Pagination
    hasMore,
    allJobsLoaded,
    currentOffset,
    
    // Actions
    handleLoadMore,
    refetchAll,
    autoBackfillIfNeeded,
    
    // Apollo client methods (for advanced usage)
    refetch,
    fetchMore,
    networkStatus
  };
}

export default useAssignmentsInfiniteScroll;
